<template>
  <div class="page">
    <reservation-header
      :status="form.id ? 'Saved' : 'New'"
      :title="'LTR kalkulacija'"
      @save="onSave"
      @delete="onDelete"
    />
    
    <div class="ltr-form-container">
      <div class="form-header">
        <!-- General Information Section -->
        <div class="general-info-section">
          <h2 class="section-title">Opšte Informacije</h2>
          <data-form2
            :key="`general-${form.id || 'new'}`"
            :data="form"
            :formFields="generalFormFields"
            :readonly="true"
            minheight="200px"
            @changed="onChanged"
          />
        </div>

        <!-- Tabs Navigation -->
        <div class="mt-4">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="mb-0">Kalkulacije i objašnjenja</h5>
            <div>
              <button type="button" class="btn btn-success btn-sm bi bi-plus" @click="addCalculation"> Dodaj kalkulaciju</button>
            </div>
          </div>
          <ul class="nav nav-tabs" role="tablist">
            <li class="nav-item" role="presentation" v-for="(calc, idx) in form.calculations" :key="idx">
              <button
                class="nav-link"
                :class="{ active: activeCalc === idx }"
                @click="activeCalc = idx"
                type="button"
                role="tab"
              >
                {{ calc.calc_title || `Kalkulacija ${idx + 1}` }}
              </button>
            </li>
          </ul>
          <div class="tab-content border border-top-0 p-3">
            <div class="tab-pane fade show active" role="tabpanel" v-if="currentCalc">
              <div class="d-flex justify-content-end mb-2">
                <button type="button" class="btn btn-outline-danger btn-sm bi bi-trash" @click="removeCalculation(activeCalc)"> Ukloni</button>
              </div>
              <!-- Totals Section -->
              <div class="form-section total-section">
                <h3 class="section-header collapsible-header" @click="toggleSection('totals')">
                  <span>Proračun</span>
                  <i class="bi" :class="expandedSections.totals ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                </h3>
                <div v-show="expandedSections.totals" class="section-content">
                  <data-form2
                    :key="`totals-${activeCalc}-${form.id || 'new'}-${calculationUpdateKey}`"
                    :data="currentCalc"
                    :formFields="totalsFormFields"
                    :readonly="true"
                    minheight="200px"
                    @changed="onCalcChanged"
                  />
                </div>
              </div>
              <!-- Maintenance Costs Section -->
              <div class="form-section">
                <h3 class="section-header collapsible-header" @click="toggleSection('maintenance')">
                  <span>Troškovi Održavanja</span>
                  <i class="bi" :class="expandedSections.maintenance ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                </h3>
                
                <div v-show="expandedSections.maintenance" class="section-content">
                
                <!-- Group 1: Leasing (2 fields) -->
                <div class="maintenance-group">
                  <h4 class="maintenance-group-title">Leasing</h4>
                  <div class="maintenance-grid">
                    <div v-for="(cost, idx) in maintenanceCosts.slice(0, 2)" :key="idx" class="maintenance-item">
                      <label class="maintenance-label">{{ cost.label }}</label>
                      <div class="maintenance-inputs">
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          v-model.number="currentCalc[cost.totalField]"
                          @blur="calculateMonthly(cost)"
                          placeholder="Ukupan trošak"
                        />
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          :value="getMonthlyValue(cost)"
                          disabled
                          tabindex="-1"
                          placeholder="Mesečni trošak"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Group 2: Osiguranje (5 fields) -->
                <div class="maintenance-group">
                  <h4 class="maintenance-group-title">Osiguranje</h4>
                  <div class="maintenance-grid">
                    <div v-for="(cost, idx) in maintenanceCosts.slice(2, 7)" :key="idx" class="maintenance-item">
                      <label class="maintenance-label">{{ cost.label }}</label>
                      <div class="maintenance-inputs">
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          v-model.number="currentCalc[cost.totalField]"
                          @blur="calculateMonthly(cost)"
                          placeholder="Ukupan trošak"
                        />
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          :value="getMonthlyValue(cost)"
                          disabled
                          tabindex="-1"
                          placeholder="Mesečni trošak"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Group 3: Gume (4 fields + tire type dropdown) -->
                <div class="maintenance-group">
                  <h4 class="maintenance-group-title">Gume</h4>
                  <div class="maintenance-grid large">
                    <!-- First row: Tire Type Dropdown + first two tire fields -->
                    <div class="maintenance-item">
                      <label class="maintenance-label">Tip guma</label>
                      <div class="tire-type-inline-controls">
                        <DropDown
                          v-if="tiresTypesLoaded"
                          :list="tiresTypes"
                          :value="currentCalc ? currentCalc.tire_type_id : null"
                          @selected="onTireTypeSelected"
                          labelfield="name"
                          clearable
                        />
                        <div v-else class="text-muted small">Loading tire types...</div>
                      </div>
                    </div>
                    <div v-for="(cost, idx) in maintenanceCosts.slice(7, 9)" :key="idx" class="maintenance-item">
                      <label class="maintenance-label">{{ cost.label }}</label>
                      <div class="maintenance-inputs">
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          v-model.number="currentCalc[cost.totalField]"
                          @blur="calculateMonthly(cost)"
                          placeholder="Ukupan trošak"
                        />
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          :value="getMonthlyValue(cost)"
                          disabled
                          tabindex="-1"
                          placeholder="Mesečni trošak"
                        />
                      </div>
                    </div>
                    <!-- Second row: remaining two tire fields -->
                    <div v-for="(cost, idx) in maintenanceCosts.slice(9, 11)" :key="idx + 9" class="maintenance-item">
                      <label class="maintenance-label">{{ cost.label }}</label>
                      <div class="maintenance-inputs">
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          v-model.number="currentCalc[cost.totalField]"
                          @blur="calculateMonthly(cost)"
                          placeholder="Ukupan trošak"
                        />
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          :value="getMonthlyValue(cost)"
                          disabled
                          tabindex="-1"
                          placeholder="Mesečni trošak"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Group 4: Servis i Usluge (8 fields) -->
                <div class="maintenance-group">
                  <h4 class="maintenance-group-title">Servis i Usluge</h4>
                  <div class="maintenance-grid large">
                    <div v-for="(cost, idx) in maintenanceCosts.slice(11, 19)" :key="idx" class="maintenance-item">
                      <label class="maintenance-label">{{ cost.label }}</label>
                      <div class="maintenance-inputs">
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          v-model.number="currentCalc[cost.totalField]"
                          @blur="calculateMonthly(cost)"
                          placeholder="Ukupan trošak"
                        />
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          :value="getMonthlyValue(cost)"
                          readonly
                          placeholder="Mesečni trošak"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Group 5: AMSS (1 field) -->
                <div class="maintenance-group">
                  <h4 class="maintenance-group-title">AMSS</h4>
                  <div class="maintenance-grid">
                    <div v-for="(cost, idx) in maintenanceCosts.slice(19, 20)" :key="idx" class="maintenance-item">
                      <label class="maintenance-label">{{ cost.label }}</label>
                      <div class="maintenance-inputs">
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          v-model.number="currentCalc[cost.totalField]"
                          @blur="calculateMonthly(cost)"
                          placeholder="Ukupan trošak"
                        />
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          :value="getMonthlyValue(cost)"
                          readonly
                          placeholder="Mesečni trošak"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Group 6: Replacement Vehicle -->
                <div class="maintenance-group">
                  <h4 class="maintenance-group-title">Zamensko vozilo</h4>
                  <div class="maintenance-grid">
                    <div class="maintenance-item full-width">
                      <label class="maintenance-label">Tip zamenskog vozila</label>
                      <div class="replacement-vehicle-inline-controls">
                        <DropDown
                          v-if="vehicleReplacementTypesLoaded"
                          :list="vehicleReplacementTypes"
                          :value="currentCalc ? currentCalc.replacement_vehicle_type_id : null"
                          @selected="onReplacementVehicleTypeSelected"
                          labelfield="name"
                          clearable
                        />
                        <div v-else class="text-muted small">Loading vehicle types...</div>
                        
                        <input
                          type="number"
                          step="1"
                          min="0"
                          class="form-control"
                          v-model.number="currentCalc.replacement_vehicle_days"
                          @input="calculateReplacementVehicleFromDays"
                          placeholder="Broj dana"
                        />
                        
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          v-model.number="currentCalc.replacement_vehicle_total"
                          @blur="calculateReplacementVehicleMonthly"
                          placeholder="Ukupan trošak"
                        />
                        
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          :value="getReplacementVehicleMonthlyValue()"
                          readonly
                          placeholder="Mesečni trošak"
                        />
                      </div>
                    </div>
                  </div>
                </div>


                </div>
              </div>

              <!-- Total Costs Section -->
              <div class="form-section total-costs-section">
                <h3 class="section-header collapsible-header" @click="toggleSection('totalCosts')">
                  <span>TOTAL TROŠKOVI</span>
                  <i class="bi" :class="expandedSections.totalCosts ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                </h3>
                
                <div v-show="expandedSections.totalCosts" class="section-content">
                <!-- Total Costs Grid -->
                <div class="total-costs-grid">
                  <!-- Ukupan trosak and Mesecni trosak -->
                  <div class="total-cost-item">
                    <label class="total-cost-label">Ukupan trošak</label>
                    <div class="total-cost-inputs">
                      <input
                        type="number"
                        step="0.01"
                        class="form-control"
                        :value="currentCalc.total_cost ? parseFloat(currentCalc.total_cost).toFixed(2) : '0.00'"
                        disabled
                        tabindex="-1"
                      />
                      <input
                        type="number"
                        step="0.01"
                        class="form-control"
                        :value="currentCalc.monthly_cost ? parseFloat(currentCalc.monthly_cost).toFixed(2) : '0.00'"
                        disabled
                        tabindex="-1"
                      />
                    </div>
                  </div>

                  <!-- Marza -->
                  <div class="total-cost-item">
                    <label class="total-cost-label">Marža</label>
                    <div class="total-cost-inputs">
                      <div class="input-group">
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          v-model.number="currentCalc.margin_percentage"
                          placeholder="Marža %"
                          @blur="calculateMargins"
                        />
                        <span class="input-group-text">%</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        class="form-control"
                        :value="currentCalc.total_margin ? parseFloat(currentCalc.total_margin).toFixed(2) : '0.00'"
                        disabled
                        tabindex="-1"
                      />
                      <input
                        type="number"
                        step="0.01"
                        class="form-control"
                        :value="currentCalc.monthly_margin ? parseFloat(currentCalc.monthly_margin).toFixed(2) : '0.00'"
                        disabled
                        tabindex="-1"
                      />
                    </div>
                  </div>

                  <!-- Ukupan trosak sa maržom -->
                  <div class="total-cost-item">
                    <label class="total-cost-label">Ukupan trošak sa maržom</label>
                    <div class="total-cost-inputs">
                      <input
                        type="number"
                        step="0.01"
                        class="form-control"
                        :value="currentCalc.total_cost_with_margin ? parseFloat(currentCalc.total_cost_with_margin).toFixed(2) : '0.00'"
                        disabled
                        tabindex="-1"
                      />
                      <input
                        type="number"
                        step="0.01"
                        class="form-control"
                        :value="currentCalc.monthly_cost_with_margin ? parseFloat(currentCalc.monthly_cost_with_margin).toFixed(2) : '0.00'"
                        disabled
                        tabindex="-1"
                      />
                    </div>
                  </div>

                  <!-- Europcar fee -->
                  <div class="total-cost-item">
                    <label class="total-cost-label">Europcar fee</label>
                    <div class="total-cost-inputs">
                      <div class="input-group">
                        <input
                          type="number"
                          step="0.01"
                          class="form-control"
                          v-model.number="currentCalc.europcar_fee_percentage"
                          placeholder="Europcar fee %"
                          @blur="calculateEuropcarFees"
                        />
                        <span class="input-group-text">%</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        class="form-control"
                        :value="currentCalc.total_europcar_fee ? parseFloat(currentCalc.total_europcar_fee).toFixed(2) : '0.00'"
                        disabled
                        tabindex="-1"
                      />
                      <input
                        type="number"
                        step="0.01"
                        class="form-control"
                        :value="currentCalc.monthly_europcar_fee ? parseFloat(currentCalc.monthly_europcar_fee).toFixed(2) : '0.00'"
                        disabled
                        tabindex="-1"
                      />
                    </div>
                  </div>
                </div>
                
                <!-- Total Price Display -->
                <div class="total-price-display mt-4">
                  <div class="total-price-item">
                    <h4 class="total-price-label">Ukupna cena bez PDV-a</h4>
                    <div class="total-price-inputs">
                      <div class="total-price-value">
                        {{ ((currentCalc.total_cost_with_margin || 0) + (currentCalc.total_europcar_fee || 0)).toFixed(2) }} EUR
                      </div>
                      <div class="total-price-value">
                        {{ (((currentCalc.total_cost_with_margin || 0) + (currentCalc.total_europcar_fee || 0)) / (form.contract_duration || 12)).toFixed(2) }} EUR/mesec
                      </div>
                    </div>
                  </div>
                </div>
                
                </div>
              </div>

              <!-- Installments Section -->
              <div class="form-section">
                <h3 class="section-header collapsible-header" @click="toggleSection('installments')">
                  <span>Rata</span>
                  <i class="bi" :class="expandedSections.installments ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                </h3>
                <div v-show="expandedSections.installments" class="section-content">

                <!-- Installments Summary -->
                <div class="installments-summary">
                  <span class="summary-text">
                    <strong>Vrednost rata:</strong> <span class="value">{{ (currentCalc.installment_value || 0).toLocaleString() }}</span> | 
                    <strong>Razlika:</strong> <span class="value">{{ (currentCalc.installment_difference || 0).toLocaleString() }}</span>
                  </span>
                </div>

                <!-- Installments Grid -->
                <div class="installments-grid">
                  <!-- BRUTO Section -->
                  <div class="installment-group">
                    <h4 class="installment-group-title">BRUTO</h4>
                    <div class="installment-items">
                      <div class="installment-item">
                        <label class="installment-label">1. rata</label>
                        <div class="installment-value-display">
                          <input
                            type="text"
                            class="form-control"
                            :value="(currentCalc.first_installment_bruto || 0).toLocaleString() + ' €'"
                            readonly
                          />
                        </div>
                      </div>
                      <div class="installment-item">
                        <label class="installment-label">47 rata</label>
                        <div class="installment-value-display">
                          <input
                            type="text"
                            class="form-control"
                            :value="(currentCalc.last_installment_bruto || 0).toLocaleString() + ' €'"
                            readonly
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- NETO Section -->
                  <div class="installment-group">
                    <h4 class="installment-group-title">NETO <span class="percentage-indicator">20%</span></h4>
                    <div class="installment-items">
                      <div class="installment-item">
                        <label class="installment-label">1. rata</label>
                        <div class="installment-value-display">
                          <input
                            type="text"
                            class="form-control"
                            :value="(currentCalc.first_installment_neto || 0).toLocaleString('en', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' €'"
                            readonly
                          />
                        </div>
                      </div>
                      <div class="installment-item">
                        <label class="installment-label">47 rata</label>
                        <div class="installment-value-display">
                          <input
                            type="text"
                            class="form-control"
                            :value="(currentCalc.last_installment_neto || 0).toLocaleString('en', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' €'"
                            readonly
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                </div>
              </div>
            </div>
            <div v-else class="text-muted small">Nema definisanih kalkulacija.</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DataForm2 from "../widgets/DataForm2.vue";
import LtrCalcService from "../../services/ltr-calculation.service";
import VehicleReplacementTypeDataService from "../../services/vehicle-replacement-type.service";
import TiresTypeDataService from "../../services/tires-type.service";
import ReservationHeader from "../reservation/header/ReservationHeader.vue";
import DropDown from "../widgets/DropDown.vue";
import utils from "../../utils/common";

export default {
  name: "ltr-calculation-form",
  components: { DataForm2, ReservationHeader, DropDown },
  // When used as a page, id will come from route params; when used as a modal, it can be passed as prop
  props: { id: { type: [Number, String], required: false } },
  data() {
    return {
      form: {
        id: null,
        title: "",
        period_from: new Date().toISOString().substring(0,10),
        period_to: new Date().toISOString().substring(0,10),
        status: 'Draft',
        // General information fields
        customer: "",
        date: new Date().toISOString().substring(0,10),
        financier: "",
        manufacturer: "",
        vehicle_type_id: 1,
        vehicle_model: "",
        tire_dimensions: "",
        exchange_rate: 117,
        vehicle_value_list_pdv: null,
        vehicle_value_list_pdv_discounted: null,
        contract_duration: null,
        vehicle_value_remaining: null,
        vehicle_value_remaining_amount: null, // calculated field
        vehicle_value_increased: null,
        total_mileage: null,
        additional_10000_km: null,
        additional_1_km: null,
        // Additional data fields
        note: "",
        bank: null,
        category: null,
        amount: null,
        calculations: [],
      },
      activeCalc: 0,
      calculationUpdateKey: 0, // Force reactivity for calculations
      expandedSections: {
        totals: true,          // First section expanded by default
        maintenance: false,    // Others closed by default
        totalCosts: false,
        installments: false
      },
      maintenanceCosts: [
        { label: 'ZAKUP VOZILA/LEASING', totalField: 'leasing_total', monthlyField: 'leasing_monthly' },
        { label: 'Manipulativni troškovi', totalField: 'handling_costs_total', monthlyField: 'handling_costs_monthly' },
        { label: 'Auto kasko', totalField: 'auto_kasko_total', monthlyField: 'auto_kasko_monthly' },
        { label: 'REGISTRACIJA VOZILA', totalField: 'registration_total', monthlyField: 'registration_monthly' },
        { label: 'OBAVEZNA OPREMA', totalField: 'mandatory_equipment_total', monthlyField: 'mandatory_equipment_monthly' },
        { label: 'AO i licno osiguranje', totalField: 'ao_personal_insurance_total', monthlyField: 'ao_personal_insurance_monthly' },
        { label: 'Zeleni karton', totalField: 'green_card_total', monthlyField: 'green_card_monthly' },
        { label: 'Gume letnje 4/4', totalField: 'summer_tires_total', monthlyField: 'summer_tires_monthly' },
        { label: 'Gume zimske 4/4', totalField: 'winter_tires_total', monthlyField: 'winter_tires_monthly' },
        { label: 'Gume skladištenje - za 4 gume mesečno', totalField: 'tire_storage_total', monthlyField: 'tire_storage_monthly' },
        { label: 'ZAMENA GUMA', totalField: 'tire_replacement_total', monthlyField: 'tire_replacement_monthly' },
        { label: 'Mehanicka zaštita - igla', totalField: 'mechanical_protection_total', monthlyField: 'mechanical_protection_monthly' },
        { label: 'Elektronska zaštita - KOBRA ALARM', totalField: 'electronic_protection_total', monthlyField: 'electronic_protection_monthly' },
        { label: 'GPS pracenje', totalField: 'gps_tracking_total', monthlyField: 'gps_tracking_monthly' },
        { label: 'GPS ugradnja', totalField: 'gps_installation_total', monthlyField: 'gps_installation_monthly' },
        { label: 'REDOVNO SERVISIRANJE', totalField: 'regular_service_total', monthlyField: 'regular_service_monthly' },
        { label: 'Vanredno održavanje', totalField: 'emergency_maintenance_total', monthlyField: 'emergency_maintenance_monthly' },
        { label: 'Produžena garancija', totalField: 'extended_warranty_total', monthlyField: 'extended_warranty_monthly' },
        { label: 'Jockey service', totalField: 'jockey_service_total', monthlyField: 'jockey_service_monthly' },
        { label: 'AMSS kartica', totalField: 'amss_card_total', monthlyField: 'amss_card_monthly' }
      ],
      vehicleReplacementTypes: [],
      vehicleReplacementTypesLoaded: false,
      tiresTypes: [],
      tiresTypesLoaded: false,
    };
  },
  computed: {
    generalFormFields() {
      return {
        rows: [
          [{ id: { type: 'hidden' } }],
          [
            { title: { label: 'Naziv kalkulacije', type: 'text' } },
            { status: { label: 'Status', type: 'dropdown', list: [ { id: 'Draft', name: 'Draft' }, 
            { id: 'Final', name: 'Final' } ] } },
            { date: { label: 'Datum', type: 'date' } },
            { vehicle_type_id: { label: "Tip vozila",
                type: "dropdown",
                url: "/vehicle-type",
                dropdownHeight: "200px", } },
          ],
          [{ divider: { type: "divider" } }],
          [
            { financier: { label: 'Finansijer', type: 'text' } },
            { customer: { label: 'Kupac (Customer)', type: 'text' } },
            { exchange_rate: {label: 'Kurs', type: 'number'}}, {}
          ],
          [
            { manufacturer: { label: 'Proizvođač (Manufacturer)', type: 'text' } },
            { vehicle_model: { label: 'Model (Motor, Oprema)', type: 'text' } },
            { tire_dimensions: { label: 'Dimenzija guma', type: 'text' } },
            {},
          ],
          [{ divider: { type: "divider" } }],
          [
            { vehicle_value_list_pdv: { label: 'Vrednost vozila sa PDV-om (bez popusta)', type: 'number' } },
            { vehicle_value_list_pdv_discounted: { label: 'Vrednost vozila sa PDV-om (sa popustom)', type: 'number' } },
            { vehicle_value_net: { label: 'Neto vrednost vozila', type: 'number', disabled: true } },
            { vehicle_value_vat: { label: 'PDV na vrednost vozila', type: 'number', disabled: true } },
          ],
          [
            { vehicle_value_insured: { label: 'Osigurana vrednost vozila', type: 'number', disabled: true } },
            { contract_duration: { label: 'Period trajanja ugovora', type: 'number' } },
            { vehicle_value_remaining: { label: 'Ostatak vrednosti (%)', type: 'number' } },
            { vehicle_value_remaining_amount: { label: 'Ostatak vrednosti sa PDV-om', type: 'number', disabled: true } },
          ],
          [
            { vehicle_value_increased: { label: 'Uvecana otkupna vrednost za 5% za klijenta', type: 'number', disabled: true } },
            { total_mileage: { label: 'Ukupna kilometraža', type: 'number' } },
            { annual_mileage: { label: 'Godišnja kilometraža', type: 'number', disabled: true } },
            { monthly_mileage: { label: 'Mesečna kilometraža', type: 'number', disabled: true } },
          ],
          [
            { additional_10000_km: { label: 'Dodatnih 10 000 km sa PDV-om', type: 'number' } },
            { additional_1_km: { label: 'Dodatni 1 km sa PDV-om', type: 'number' } },
            {}, {}
          ],
        ],
      };
    },
    totalsFormFields() {
      return {
        rows: [
          [
            { vehicle_value_with_vat: { label: 'Vrednost sa PDV', type: 'number', disabled: true } },
            { vehicle_value_without_vat: { label: 'Vrednost bez PDV', type: 'number', disabled: true } },
            { participation_percentage: { label: 'Učešće od neto cene (%)', type: 'number' } },
            { participation_amount: { label: 'Učešće od neto cene (EUR)', type: 'number', disabled: true } },
          ],
          [
            { financed_pdv: { label: 'Finanisiran PDV', type: 'number', disabled: true } },
            { financing_amount: { label: 'Iznos finansiranja', type: 'number', disabled: true } },
            { residual_value_percentage: { label: 'Ostatak bez PDV (%)', type: 'number' } },
            { residual_value_amount: { label: 'Ostatak bez PDV (EUR)', type: 'number', disabled: true } },
          ],
          [{ divider: { type: "divider" } }],
          [
            { period_months: { label: 'Period (meseci)', type: 'number', disabled: true } },
            { reprogramming_note: { label: 'Reprogramiranje', type: 'dropdown',
                list: [ { id: 'Ne reprogramira se', name: 'Ne reprogramira se' }, 
                { id: '6 Mesecni euribor', name: '6 Mesecni euribor' } ,
              { id: '3 Mesecni euribor', name: '3 Mesecni euribor'}],
              empty: true,
             } },
            { interest_rate: { label: 'Kamatna stopa (%)', type: 'number' } },
            { vat_financing_rate: { label: 'Kamatna stopa finan. PDV (%)', type: 'number' } },
          ],
          [
            { processing_fee_percentage: { label: 'Troškovi obrade sa PDV (%)', type: 'number' } },
            { processing_fee_amount: { label: 'Troškovi obrade sa PDV (EUR)', type: 'number', disabled: true } },
            { monthly_installment_net: { label: 'Mesečna rata Neto', type: 'number', disabled: true } },
            { financed_pdv_monthly: { label: 'Finanisiran PDV mesecno', type: 'number', disabled: true } },
          ],
        ]
      }
    },
    currentCalc() {
      return this.form.calculations && this.form.calculations.length > 0
        ? this.form.calculations[this.activeCalc]
        : null;
    },
    selectedReplacementVehicleType() {
      if (!this.currentCalc || !this.currentCalc.replacement_vehicle_type_id) {
        return null;
      }
      return this.vehicleReplacementTypes.find(type => 
        type.id === this.currentCalc.replacement_vehicle_type_id
      );
    },
    selectedVehicleType() {
      // This will be used to display the selected vehicle type name
      // The actual dropdown data will be loaded by DataForm2 component from the URL
      return this.form.vehicle_type_id;
    },
    selectedTireType() {
      if (!this.currentCalc || !this.currentCalc.tire_type_id) {
        return null;
      }
      return this.tiresTypes.find(type => 
        type.id === this.currentCalc.tire_type_id
      );
    }
  },
  watch: {
    // Watch for form data changes to ensure reactivity
    'form': {
      handler(newVal) {
        console.log('Form data changed:', newVal);
      },
      deep: true
    },
    // Watch for route parameter changes
    '$route.params.id': {
      handler(newId, oldId) {
        if (newId !== oldId) {
          console.log('Route ID changed from', oldId, 'to', newId);
          this.loadData();
        }
      }
    }
  },
  methods: {
    onChanged(val, key) {
      console.log('Form field changed:', key, '=', val);
      this.form[key] = val;
      
      // Perform automatic calculations based on changed field
      this.performGeneralCalculations(key);
    },
    performGeneralCalculations(changedField) {
      // Calculate vehicle_value_net from vehicle_value_list_pdv_discounted
      // Note: Net value = Gross value / 1.2 (removing 20% VAT)
      if (changedField === 'vehicle_value_list_pdv_discounted' || !changedField) {
        if (this.form.vehicle_value_list_pdv_discounted) {
          this.form.vehicle_value_net = Math.round((this.form.vehicle_value_list_pdv_discounted / 1.2) * 100) / 100;
        }
      }
      
      // Calculate vehicle_value_vat from the difference
      if (changedField === 'vehicle_value_list_pdv_discounted' || !changedField) {
        if (this.form.vehicle_value_list_pdv_discounted && this.form.vehicle_value_net) {
          this.form.vehicle_value_vat = Math.round((this.form.vehicle_value_list_pdv_discounted - this.form.vehicle_value_net) * 100) / 100;
        }
      }
      
      // Calculate vehicle_value_insured = vehicle_value_net
      if (changedField === 'vehicle_value_list_pdv_discounted' || !changedField) {
        if (this.form.vehicle_value_net) {
          this.form.vehicle_value_insured = this.form.vehicle_value_net;
        }
      }
      
      // Calculate vehicle_value_remaining_amount from vehicle_value_list_pdv_discounted and vehicle_value_remaining percentage
      if (changedField === 'vehicle_value_list_pdv_discounted' || changedField === 'vehicle_value_remaining' || !changedField) {
        if (this.form.vehicle_value_list_pdv_discounted && this.form.vehicle_value_remaining) {
          this.form.vehicle_value_remaining_amount = Math.round((this.form.vehicle_value_list_pdv_discounted * this.form.vehicle_value_remaining / 100) * 100) / 100;
        }
      }
      
      // Calculate vehicle_value_increased = vehicle_value_remaining_amount + 5%
      if (changedField === 'vehicle_value_list_pdv_discounted' || changedField === 'vehicle_value_remaining' || !changedField) {
        if (this.form.vehicle_value_remaining_amount) {
          this.form.vehicle_value_increased = Math.round((this.form.vehicle_value_remaining_amount * 1.05) * 100) / 100;
        }
      }
      
      // Calculate annual_mileage and monthly_mileage from total_mileage and contract_duration
      if (changedField === 'total_mileage' || changedField === 'contract_duration' || !changedField) {
        if (this.form.total_mileage && this.form.contract_duration) {
          // annual_mileage = total_mileage / (contract_duration / 12) 
          // This assumes contract_duration is in months, so we divide by contract_duration/12 to get years
          this.form.annual_mileage = Math.round((this.form.total_mileage / (this.form.contract_duration / 12)) * 100) / 100;
          
          // monthly_mileage = annual_mileage / 12
          if (this.form.annual_mileage) {
            this.form.monthly_mileage = Math.round((this.form.annual_mileage / 12) * 100) / 100;
          }
        }
        
        // Recalculate replacement vehicle costs when contract_duration changes
        if (changedField === 'contract_duration') {
          this.calculateReplacementVehicleFromDays();
        }
      }
      
      // Update totals fields in current calculation
      this.updateTotalsFields();
      
      // Trigger calculation updates for the current calculation
      if (this.currentCalc) {
        this.performCalculations();
      }
    },
    
    updateTotalsFields() {
      if (this.currentCalc) {
        // vehicle_value_with_vat = vehicle_value_list_pdv_discounted
        this.currentCalc.vehicle_value_with_vat = this.form.vehicle_value_list_pdv_discounted;
        
        // vehicle_value_without_vat = vehicle_value_net
        this.currentCalc.vehicle_value_without_vat = this.form.vehicle_value_net;
        
        // period_months = contract_duration
        this.currentCalc.period_months = this.form.contract_duration;
      }
    },
    toggleSection(sectionName) {
      this.expandedSections[sectionName] = !this.expandedSections[sectionName];
    },
    hydrateFlatFromData() {
      // This method is no longer needed since data is now stored directly in separate fields
      // Data is now loaded directly into the form fields from the API response
      return;
    },
    packDataFromFlat() {
      // This method is no longer needed since data is now stored directly in separate fields
      // Data will be sent directly as individual fields to the API
      return;
    },
    addCalculation() {
      // If there's a current calculation, copy all its values to the new one
      let newCalculation;
      if (this.currentCalc) {
        // Deep copy the current calculation
        newCalculation = JSON.parse(JSON.stringify(this.currentCalc));
        // Set the title to next calculation number
        newCalculation.calc_title = `Kalkulacija ${this.form.calculations.length + 1}`;
      } else {
        // If no current calculation exists, create a default one
        newCalculation = {
          calc_title: '',
          calc_type: null,
          vehicle_value_gross: null,
          vehicle_value_net: null,
          discount_pct: null,
          discount_eur: null,
          vehicle_value_after_discount: null,
          months: 12,
          interest_rate_pct: null,
          fee_pct: null,
          fee_eur: null,
          residual_pct: 0,
          residual_eur: null,
          monthly_price_eur: null,
          explanation: '',
          // Maintenance costs
          leasing_total: null,
          leasing_monthly: null,
          handling_costs_total: null,
          handling_costs_monthly: null,
          auto_kasko_total: null,
          auto_kasko_monthly: null,
          registration_total: null,
          registration_monthly: null,
          mandatory_equipment_total: null,
          mandatory_equipment_monthly: null,
          ao_personal_insurance_total: null,
          ao_personal_insurance_monthly: null,
          green_card_total: null,
          green_card_monthly: null,
          summer_tires_total: null,
          summer_tires_monthly: null,
          winter_tires_total: null,
          winter_tires_monthly: null,
          tire_storage_total: null,
          tire_storage_monthly: null,
          tire_replacement_total: null,
          tire_replacement_monthly: null,
          mechanical_protection_total: null,
          mechanical_protection_monthly: null,
          electronic_protection_total: null,
          electronic_protection_monthly: null,
          gps_tracking_total: null,
          gps_tracking_monthly: null,
          gps_installation_total: null,
          gps_installation_monthly: null,
          regular_service_total: null,
          regular_service_monthly: null,
          emergency_maintenance_total: null,
          emergency_maintenance_monthly: null,
          extended_warranty_total: null,
          extended_warranty_monthly: null,
          jockey_service_total: null,
          jockey_service_monthly: null,
          amss_card_total: null,
          amss_card_monthly: null,
          // Replacement vehicle fields
          replacement_vehicle_type_id: 1,
          replacement_vehicle_days: null,
          replacement_vehicle_total: null,
          replacement_vehicle_monthly: null,
          // Tire type fields
          tire_type_id: null,
          // Financing calculation fields
          vehicle_value_with_vat: null,
          vehicle_value_without_vat: null,
          participation_percentage: null,
          participation_amount: null,
          financing_amount: null,
          residual_value_percentage: null,
          residual_value_amount: null,
          // Total costs fields
          total_cost: null,
          monthly_cost: null,
          margin_percentage: null,
          total_margin: null,
          monthly_margin: null,
          total_cost_with_margin: null,
          monthly_cost_with_margin: null,
          europcar_fee_percentage: null,
          total_europcar_fee: null,
          monthly_europcar_fee: null,
          // Financing fields
          participation_pct: 0,
          participation_eur: null,
          financed_pdv: null,
          // Installments
          installment_value: null,
          installment_difference: null,
          first_installment_bruto: null,
          last_installment_bruto: null,
          first_installment_neto: null,
          last_installment_neto: null,
          regular_installment_bruto: null,
          regular_installment_neto: null,
          // Totals
          total_financing_cost: null,
          total_all_costs: null,
          total_bruto_monthly: null,
          total_neto_monthly: null,
        };
        
        // Set default dropdown values for the new calculation only if no current calc exists
        if (this.tiresTypes.length > 0) {
          newCalculation.tire_type_id = this.tiresTypes[0].id;
        }
        if (this.vehicleReplacementTypes.length > 0) {
          newCalculation.replacement_vehicle_type_id = this.vehicleReplacementTypes[0].id;
        }
      }
      
      // Add the new calculation to the array
      this.form.calculations.push(newCalculation);
      this.activeCalc = this.form.calculations.length - 1;
      
      // Update totals fields for the new calculation
      this.updateTotalsFields();
      
      // Perform all calculations for the new calculation
      this.performCalculations();
    },
    removeCalculation(idx) {
      if (idx < 0 || idx >= this.form.calculations.length) return;
      this.form.calculations.splice(idx, 1);
      if (this.activeCalc >= this.form.calculations.length) {
        this.activeCalc = this.form.calculations.length - 1;
      }
    },
    onCalcChanged(val, key) {
      console.log('Calculation field changed:', key, '=', val);
      if (this.currentCalc) {
        this.currentCalc[key] = val;
      }
      this.performCalculations();
    },
    calculateMonthly(cost) {
      if (this.currentCalc && this.currentCalc[cost.totalField]) {
        const months = this.form.contract_duration || 12;
        this.currentCalc[cost.monthlyField] = this.currentCalc[cost.totalField] / months;
      }
    },
    getMonthlyValue(cost) {
      if (this.currentCalc && this.currentCalc[cost.totalField]) {
        const months = this.form.contract_duration || 12;
        return (this.currentCalc[cost.totalField] / months).toFixed(2);
      }
      return '0.00';
    },
    // Replacement vehicle methods
    onReplacementVehicleTypeSelected(typeId) {
      if (this.currentCalc) {
        this.currentCalc.replacement_vehicle_type_id = typeId;
        // Trigger automatic calculation when type is selected
        this.calculateReplacementVehicleFromDays();
      }
    },
    calculateReplacementVehicleFromDays() {
      if (this.currentCalc && this.currentCalc.replacement_vehicle_days && this.currentCalc.replacement_vehicle_type_id && this.form.contract_duration) {
        // Find the selected vehicle replacement type to get its price_per_month
        const selectedType = this.vehicleReplacementTypes.find(type => 
          type.id === this.currentCalc.replacement_vehicle_type_id
        );
        
        if (selectedType && selectedType.price_per_month) {
          // Formula: (price_per_month / 30) * days * (contract_duration / 12)
          const dailyRate = selectedType.price_per_month / 30;
          const totalDays = this.currentCalc.replacement_vehicle_days;
          const contractYears = this.form.contract_duration / 12;
          
          this.currentCalc.replacement_vehicle_total = Math.round((dailyRate * totalDays * contractYears) * 100) / 100;
          
          // Recalculate monthly value
          this.calculateReplacementVehicleMonthly();
        }
      }
    },
    calculateReplacementVehicleMonthly() {
      if (this.currentCalc && this.currentCalc.replacement_vehicle_total) {
        const months = this.form.contract_duration || 12;
        this.currentCalc.replacement_vehicle_monthly = this.currentCalc.replacement_vehicle_total / months;
        this.performCalculations();
      }
    },
    getReplacementVehicleMonthlyValue() {
      if (this.currentCalc && this.currentCalc.replacement_vehicle_total) {
        const months = this.form.contract_duration || 12;
        return (this.currentCalc.replacement_vehicle_total / months).toFixed(2);
      }
      return '0.00';
    },
    async loadVehicleReplacementTypes() {
      try {
        console.log('Loading vehicle replacement types...');
        const response = await VehicleReplacementTypeDataService.getAll();
        console.log('Response:', response);
        this.vehicleReplacementTypes = response.data.rows || response.data || [];
        console.log('Vehicle replacement types loaded:', this.vehicleReplacementTypes);
        this.vehicleReplacementTypesLoaded = true;
        
        // Set default value to first item if available and no value is set
        if (this.vehicleReplacementTypes.length > 0 && this.currentCalc && !this.currentCalc.replacement_vehicle_type_id) {
          this.currentCalc.replacement_vehicle_type_id = this.vehicleReplacementTypes[0].id;
        }
        
        // Set default values for all calculations if both datasets are loaded
        this.checkAndSetDefaults();
      } catch (error) {
        console.error('Error loading vehicle replacement types:', error);
        this.vehicleReplacementTypes = [];
        this.vehicleReplacementTypesLoaded = true;
      }
    },
    async loadTiresTypes() {
      try {
        console.log('Loading tires types...');
        const response = await TiresTypeDataService.getAll();
        console.log('Response:', response);
        this.tiresTypes = response.data.rows || response.data || [];
        console.log('Tires types loaded:', this.tiresTypes);
        this.tiresTypesLoaded = true;
        
        // Set default value to first item if available and no value is set
        if (this.tiresTypes.length > 0 && this.currentCalc && !this.currentCalc.tire_type_id) {
          this.currentCalc.tire_type_id = this.tiresTypes[0].id;
        }
        
        // Set default values for all calculations if both datasets are loaded
        this.checkAndSetDefaults();
      } catch (error) {
        console.error('Error loading tires types:', error);
        this.tiresTypes = [];
        this.tiresTypesLoaded = true;
      }
    },
    // Method to check if both dropdown datasets are loaded and set defaults only for new forms
    checkAndSetDefaults() {
      if (this.tiresTypesLoaded && this.vehicleReplacementTypesLoaded) {
        // Only set defaults if this is a new form
        const pid = this.id ?? this.$route?.params?.id;
        if (!pid || pid === 'new') {
          this.setDefaultDropdownValues();
        }
      }
    },
    // Method to set default values for dropdown fields in all calculations
    setDefaultDropdownValues() {
      console.log('Setting default dropdown values for calculations...');
      if (this.form.calculations && this.form.calculations.length > 0) {
        this.form.calculations.forEach((calc, index) => {
          // Set default tire type if not set and data is available
          if (this.tiresTypes.length > 0 && (!calc.tire_type_id || calc.tire_type_id === null)) {
            console.log(`Setting default tire type for calculation ${index}:`, this.tiresTypes[0]);
            calc.tire_type_id = this.tiresTypes[0].id;
          }
          
          // Set default replacement vehicle type if not set and data is available
          if (this.vehicleReplacementTypes.length > 0 && (!calc.replacement_vehicle_type_id || calc.replacement_vehicle_type_id === null)) {
            console.log(`Setting default replacement vehicle type for calculation ${index}:`, this.vehicleReplacementTypes[0]);  
            calc.replacement_vehicle_type_id = this.vehicleReplacementTypes[0].id;
          }
        });
      }
    },
    // Tire type methods
    onTireTypeSelected(typeId) {
      if (this.currentCalc) {
        this.currentCalc.tire_type_id = typeId;
      }
    },
    calculateMargins() {
      if (this.currentCalc && this.currentCalc.total_cost && this.currentCalc.margin_percentage !== null) {
        const calc = this.currentCalc;
        const months = this.form.contract_duration || 12;
        
        calc.total_margin = calc.total_cost * (calc.margin_percentage / 100);
        calc.monthly_margin = calc.total_margin / months;
        
        // Calculate total cost with margin
        calc.total_cost_with_margin = calc.total_cost + calc.total_margin;
        calc.monthly_cost_with_margin = calc.total_cost_with_margin / months;
      }
    },
    calculateEuropcarFees() {
      if (this.currentCalc && this.currentCalc.total_cost_with_margin && this.currentCalc.europcar_fee_percentage !== null) {
        const calc = this.currentCalc;
        const months = this.form.contract_duration || 12;
        
        calc.total_europcar_fee = calc.total_cost_with_margin * (calc.europcar_fee_percentage / 100);
        calc.monthly_europcar_fee = calc.total_europcar_fee / months;
      }
    },
    calculateFinancingFields() {
      if (!this.currentCalc) return;
      
      const calc = this.currentCalc;
      
      // Calculate participation amount from percentage
      if (calc.vehicle_value_without_vat && calc.participation_percentage !== null) {
        calc.participation_amount = calc.vehicle_value_without_vat * (calc.participation_percentage / 100);
      }
      
      // Calculate financing amount
      // financing_amount = vehicle_value_without_vat
      if (calc.vehicle_value_without_vat) {
        calc.financing_amount = calc.vehicle_value_without_vat;
      }
      
      // Calculate residual value amount from percentage
      if (calc.vehicle_value_without_vat && calc.residual_value_percentage !== null) {
        calc.residual_value_amount = calc.vehicle_value_without_vat * (calc.residual_value_percentage / 100);
      }
      
      // Calculate processing fee amount from percentage
      if (calc.vehicle_value_with_vat && calc.processing_fee_percentage !== null) {
        calc.processing_fee_amount = calc.vehicle_value_with_vat * (calc.processing_fee_percentage / 100);
      }
      
      // Calculate monthly installment using PMT formula
      // monthly_installment_net = +PMT(vat_financing_rate/12,period_months,-financing_amount,residual_value_amount,0)
      if (calc.vat_financing_rate && calc.financing_amount && calc.residual_value_amount !== null && calc.period_months) {
        const monthlyRate = calc.vat_financing_rate / 100 / 12;
        const numPayments = calc.period_months; 
        const presentValue = calc.financing_amount; // Loan amount
        const futureValue = calc.residual_value_amount;
        
        if (monthlyRate > 0) {
          // Correct PMT formula: PMT = (PV - FV / (1 + r)^n) * r / (1 - (1 + r)^(-n))
          const powerTerm = Math.pow(1 + monthlyRate, numPayments);
          const adjustedPV = presentValue - (futureValue / powerTerm);
          const denominator = 1 - Math.pow(1 + monthlyRate, -numPayments);
          calc.monthly_installment_net = (adjustedPV * monthlyRate) / denominator;
        } else {
          // If no interest, simple calculation
          calc.monthly_installment_net = (presentValue - futureValue) / numPayments;
        }
        
        // Round to 2 decimal places
        calc.monthly_installment_net = Math.round(calc.monthly_installment_net * 100) / 100;
        
        // Calculate financed_pdv using the new formula (after monthly_installment_net is calculated)
        // financed_pdv = (vehicle_value_without_vat * 20/100) + (((monthly_installment_net*period_months)+residual_value_amount-vehicle_value_without_vat) * 20/100)
        if (calc.vehicle_value_without_vat) {
          const vatOnVehicleValue = calc.vehicle_value_without_vat * 20 / 100;
          const netFinancingAmount = (calc.monthly_installment_net * calc.period_months) + calc.residual_value_amount - calc.vehicle_value_without_vat;
          const vatOnNetFinancing = netFinancingAmount * 20 / 100;
          calc.financed_pdv = Math.round((vatOnVehicleValue + vatOnNetFinancing) * 100) / 100;
        }
      }
      
      // Calculate financed PDV monthly (usually 0 for this type of calculation)
      calc.financed_pdv_monthly = 0.00;
      
      // Calculate leasing costs based on monthly installment
      if (calc.monthly_installment_net && calc.period_months) {
        calc.leasing_monthly = calc.monthly_installment_net;
        calc.leasing_total = calc.leasing_monthly * calc.period_months;
      }
      
      // Calculate handling costs based on vehicle value
      if (this.form.vehicle_value_net) {
        calc.handling_costs_total = Math.round((this.form.vehicle_value_net * 0.0015) * 100) / 100;
        if (calc.period_months) {
          calc.handling_costs_monthly = Math.round((calc.handling_costs_total / calc.period_months) * 100) / 100;
        }
      }
      
      // Trigger Vue reactivity update
      this.calculationUpdateKey++;
    },
    performCalculations() {
      if (!this.currentCalc) return;
      
      // Update totals fields first
      this.updateTotalsFields();
      
      const calc = this.currentCalc;
      
      // Calculate participation EUR
      if (calc.vehicle_value_net && calc.participation_pct !== null) {
        calc.participation_eur = calc.vehicle_value_net * (calc.participation_pct / 100);
      }
      
      // Note: financed_pdv is now calculated in calculateFinancingFields() using the new formula
      
      // Calculate residual EUR
      if (calc.vehicle_value_net && calc.residual_pct !== null) {
        calc.residual_eur = calc.vehicle_value_net * (calc.residual_pct / 100);
      }
      
      // Calculate monthly maintenance costs
      this.maintenanceCosts.forEach(cost => {
        this.calculateMonthly(cost);
      });
      
      // Calculate total maintenance monthly
      const totalMaintenanceMonthly = this.maintenanceCosts.reduce((sum, cost) => {
        return sum + (calc[cost.monthlyField] || 0);
      }, 0);
      
      // Calculate replacement vehicle monthly cost
      const replacementVehicleMonthly = calc.replacement_vehicle_monthly || 0;
      
      // Calculate total maintenance costs
      const totalMaintenanceCosts = this.maintenanceCosts.reduce((sum, cost) => {
        return sum + (calc[cost.totalField] || 0);
      }, 0);
      
      // Calculate replacement vehicle total cost
      const replacementVehicleTotal = calc.replacement_vehicle_total || 0;
      
      // Update total costs (including replacement vehicle)
      calc.total_cost = totalMaintenanceCosts + replacementVehicleTotal;
      calc.monthly_cost = totalMaintenanceMonthly + replacementVehicleMonthly;
      
      // Recalculate margins and fees if percentages are set
      this.calculateMargins();
      this.calculateEuropcarFees();
      
      // Calculate financing fields
      this.calculateFinancingFields();
      
      // Calculate totals (simplified - would need more complex financial formulas in reality)
      calc.total_financing_cost = (calc.monthly_price_eur || 0) * (this.form.contract_duration || 12);
      calc.total_all_costs = calc.total_financing_cost + this.maintenanceCosts.reduce((sum, cost) => sum + (calc[cost.totalField] || 0), 0);
      
      // Calculate total monthly rates
      calc.total_neto_monthly = (calc.monthly_price_eur || 0) + totalMaintenanceMonthly;
      calc.total_bruto_monthly = calc.total_neto_monthly * 1.2; // Adding 20% for PDV
      
      // Update installments (simplified calculation)
      calc.regular_installment_neto = calc.total_neto_monthly;
      calc.regular_installment_bruto = calc.total_bruto_monthly;
      calc.first_installment_neto = calc.regular_installment_neto + (calc.financed_pdv || 0);
      calc.first_installment_bruto = calc.regular_installment_bruto + (calc.financed_pdv || 0);
      
      // Calculate installment_value and installment_difference
      const ukupnaCenaBezPdvMesecno = (((calc.total_cost_with_margin || 0) + (calc.total_europcar_fee || 0)) / (this.form.contract_duration || 12));
      calc.installment_value = ukupnaCenaBezPdvMesecno * (this.form.contract_duration || 12) * 1.2;
      calc.installment_difference = calc.installment_value + (this.form.vehicle_value_remaining_amount || 0) - (this.form.vehicle_value_list_pdv_discounted || 0);
      
      // Calculate first_installment_neto based on monthly total price without VAT * 20%
      calc.first_installment_neto = ukupnaCenaBezPdvMesecno * (this.form.contract_duration || 12) * 0.2;
      
      // Calculate last_installment_neto = (ukupnaCenaBezPdvMesecno * contract_duration - first_installment_neto) / (contract_duration - 1)
      const contractDuration = this.form.contract_duration || 12;
      if (contractDuration > 1) {
        calc.last_installment_neto = (ukupnaCenaBezPdvMesecno * contractDuration - calc.first_installment_neto) / (contractDuration - 1);
      } else {
        calc.last_installment_neto = 0;
      }
      
      // Calculate BRUTO installments based on NETO values * 1.2 (adding 20% VAT)
      calc.first_installment_bruto = calc.first_installment_neto * 1.2;
      calc.last_installment_bruto = calc.last_installment_neto * 1.2;
      
      // Trigger Vue reactivity update
      this.calculationUpdateKey++;
    },
    async loadData() {
      const pid = this.id ?? this.$route?.params?.id;
      if (!pid || pid === 'new') return;

      try {
        const { data } = await LtrCalcService.get(pid);

        console.log('Raw data from server:', data);

        // Merge the loaded data with the form, ensuring all fields are populated
        this.form = {
          ...this.form,
          ...data,
          calculations: data.calculations.map(calc => ({
            ...calc,
            // Ensure all fields are initialized, even if missing in the response
            months: calc.months ?? 12,
            fee_eur: calc.fee_eur ?? null,
            fee_pct: calc.fee_pct ?? null,
            calc_type: calc.calc_type ?? null,
            calc_title: calc.calc_title ?? '',
            total_cost: calc.total_cost ?? 0,
            explanation: calc.explanation ?? '',
            discount_eur: calc.discount_eur ?? null,
            discount_pct: calc.discount_pct ?? null,
            financed_pdv: calc.financed_pdv ?? null,
            monthly_cost: calc.monthly_cost ?? 0,
            residual_eur: calc.residual_eur ?? null,
            residual_pct: calc.residual_pct ?? 0,
            tire_type_id: calc.tire_type_id ?? null,
            total_margin: calc.total_margin ?? null,
            interest_rate: calc.interest_rate ?? null,
            leasing_total: calc.leasing_total ?? null,
            period_months: calc.period_months ?? null,
            monthly_margin: calc.monthly_margin ?? null,
            amss_card_total: calc.amss_card_total ?? null,
            leasing_monthly: calc.leasing_monthly ?? null,
            total_all_costs: calc.total_all_costs ?? null,
            auto_kasko_total: calc.auto_kasko_total ?? null,
            financing_amount: calc.financing_amount ?? null,
            green_card_total: calc.green_card_total ?? null,
            amss_card_monthly: calc.amss_card_monthly ?? null,
            interest_rate_pct: calc.interest_rate_pct ?? null,
            margin_percentage: calc.margin_percentage ?? null,
            monthly_price_eur: calc.monthly_price_eur ?? null,
            participation_eur: calc.participation_eur ?? null,
            participation_pct: calc.participation_pct ?? null,
            vehicle_value_net: calc.vehicle_value_net ?? null,
            auto_kasko_monthly: calc.auto_kasko_monthly ?? null,
            gps_tracking_total: calc.gps_tracking_total ?? null,
            green_card_monthly: calc.green_card_monthly ?? null,
            registration_total: calc.registration_total ?? null,
            reprogramming_note: calc.reprogramming_note ?? 'Ne reprogramira se',
            summer_tires_total: calc.summer_tires_total ?? null,
            tire_storage_total: calc.tire_storage_total ?? null,
            total_europcar_fee: calc.total_europcar_fee ?? null,
            total_neto_monthly: calc.total_neto_monthly ?? null,
            vat_financing_rate: calc.vat_financing_rate ?? null,
            winter_tires_total: calc.winter_tires_total ?? null,
            total_bruto_monthly: calc.total_bruto_monthly ?? null,
            vehicle_value_gross: calc.vehicle_value_gross ?? null,
            financed_pdv_monthly: calc.financed_pdv_monthly ?? null,
            gps_tracking_monthly: calc.gps_tracking_monthly ?? null,
            handling_costs_total: calc.handling_costs_total ?? null,
            jockey_service_total: calc.jockey_service_total ?? null,
            monthly_europcar_fee: calc.monthly_europcar_fee ?? null,
            participation_amount: calc.participation_amount ?? null,
            registration_monthly: calc.registration_monthly ?? null,
            summer_tires_monthly: calc.summer_tires_monthly ?? null,
            tire_storage_monthly: calc.tire_storage_monthly ?? null,
            total_financing_cost: calc.total_financing_cost ?? null,
            winter_tires_monthly: calc.winter_tires_monthly ?? null,
            processing_fee_amount: calc.processing_fee_amount ?? null,
            regular_service_total: calc.regular_service_total ?? null,
            residual_value_amount: calc.residual_value_amount ?? null,
            first_installment_neto: calc.first_installment_neto ?? null,
            gps_installation_total: calc.gps_installation_total ?? null,
            handling_costs_monthly: calc.handling_costs_monthly ?? null,
            jockey_service_monthly: calc.jockey_service_monthly ?? null,
            tire_replacement_total: calc.tire_replacement_total ?? null,
            vehicle_value_with_vat: calc.vehicle_value_with_vat ?? null,
            europcar_fee_percentage: calc.europcar_fee_percentage ?? null,
            extended_warranty_total: calc.extended_warranty_total ?? null,
            first_installment_bruto: calc.first_installment_bruto ?? null,
            monthly_installment_net: calc.monthly_installment_net ?? null,
            regular_service_monthly: calc.regular_service_monthly ?? null,
            gps_installation_monthly: calc.gps_installation_monthly ?? null,
            participation_percentage: calc.participation_percentage ?? null,
            regular_installment_neto: calc.regular_installment_neto ?? null,
            tire_replacement_monthly: calc.tire_replacement_monthly ?? null,
            extended_warranty_monthly: calc.extended_warranty_monthly ?? null,
            mandatory_equipment_total: calc.mandatory_equipment_total ?? null,
            processing_fee_percentage: calc.processing_fee_percentage ?? '',
            regular_installment_bruto: calc.regular_installment_bruto ?? null,
            replacement_vehicle_total: calc.replacement_vehicle_total ?? null,
            residual_value_percentage: calc.residual_value_percentage ?? null,
            vehicle_value_without_vat: calc.vehicle_value_without_vat ?? null,
            ao_personal_insurance_total: calc.ao_personal_insurance_total ?? null,
            electronic_protection_total: calc.electronic_protection_total ?? null,
            emergency_maintenance_total: calc.emergency_maintenance_total ?? null,
            mandatory_equipment_monthly: calc.mandatory_equipment_monthly ?? null,
            mechanical_protection_total: calc.mechanical_protection_total ?? null,
            replacement_vehicle_monthly: calc.replacement_vehicle_monthly ?? null,
            replacement_vehicle_days: calc.replacement_vehicle_days ?? null,
            replacement_vehicle_type_id: calc.replacement_vehicle_type_id ?? null,
            vehicle_value_after_discount: calc.vehicle_value_after_discount ?? null,
            ao_personal_insurance_monthly: calc.ao_personal_insurance_monthly ?? null,
            electronic_protection_monthly: calc.electronic_protection_monthly ?? null,
            emergency_maintenance_monthly: calc.emergency_maintenance_monthly ?? null,
            mechanical_protection_monthly: calc.mechanical_protection_monthly ?? null,
          })),
        };

        // If no calculations exist in loaded data, create a default one
        if (!this.form.calculations || this.form.calculations.length === 0) {
          this.addCalculation();
          return;
        }

        // Set active calculation to first one
        this.activeCalc = 0;

        console.log('Form after loading data:', this.form);
        console.log('Current calculation:', this.currentCalc);

        // Perform calculations after loading data
        this.performGeneralCalculations();
        
        // Update totals fields in calculations
        this.updateTotalsFields();
        
        // Perform all calculation logic for each calculation
        this.form.calculations.forEach((calc, index) => {
          const originalActiveCalc = this.activeCalc;
          this.activeCalc = index;
          this.performCalculations();
          this.activeCalc = originalActiveCalc;
        });

        // Force reactivity update
        this.$forceUpdate();

      } catch (error) {
        console.error('Error loading LTR calculation:', error);
        this.$store.commit("app/showAlert", { 
          type: "danger", 
          message: "Greška pri učitavanju kalkulacije: " + (error.response?.data?.message || error.message)
        });
      }
    },
    async onSave() {
      try {
        if (this.form.id) {
          await LtrCalcService.update(this.form.id, this.form);
        } else {
          const { data } = await LtrCalcService.create(this.form);
          this.form.id = data.id;
        }
        this.$store.commit("app/showAlert", { type: "success", message: "Sačuvano." });
        // If used as a page, navigate back to list; otherwise emit saved for modal usage
        if (this.$route && this.$route.name && this.$route.name.startsWith('ltr')) {
          this.$router.push({ name: 'ltr' });
        } else {
          this.$emit('saved', this.form);
        }
      } catch (e) {
        this.$store.commit("app/showAlert", { type: "danger", message: e.response?.data?.message || e.message });
      }
    },
    onDelete() {
      if (!this.form.id) return;
      utils.showDialog(
        this.form,
        "Da li ste sigurni da želite da obrišete LTR kalkulaciju?",
        `Brisanje LTR kalkulacije: ${this.form.title || this.form.id}`,
        async () => {
          try {
            await LtrCalcService.delete(this.form.id);
            this.$store.commit("app/showAlert", { type: "success", message: "Obrisano." });
            if (this.$route && this.$route.name && this.$route.name.startsWith('ltr')) {
              this.$router.go(-1);
            } else {
              this.$emit('deleted', this.form.id);
            }
          } catch (e) {
            this.$store.commit("app/showAlert", { type: "danger", message: e.response?.data?.message || e.message });
          }
        }
      );
    },
    onCancel() {
      if (this.$route && this.$route.name && this.$route.name.startsWith('ltr')) {
        this.$router.back();
      } else {
        this.$emit('cancel');
      }
    },
  },
  async mounted() {
    // Load dropdown data first
    await Promise.all([
      this.loadVehicleReplacementTypes(),
      this.loadTiresTypes()
    ]);
    
    // Check if this is a new form or existing one
    const pid = this.id ?? this.$route?.params?.id;
    
    if (!pid || pid === 'new') {
      // Initialize with one sample calculation for new forms
      this.form.calculations = [
        { 
          calc_title: 'Kalkulacija 1', 
          calc_type: null, 
          vehicle_value_gross: null, 
          vehicle_value_net: null, 
          discount_pct: null, 
          discount_eur: null, 
          vehicle_value_after_discount: null, 
          months: 12, 
          interest_rate_pct: null, 
          fee_pct: null, 
          fee_eur: null, 
          residual_pct: 0, 
          residual_eur: null, 
          monthly_price_eur: null, 
          explanation: '',
          // Maintenance costs
          leasing_total: null,
          leasing_monthly: null,
          handling_costs_total: null,
          handling_costs_monthly: null,
          auto_kasko_total: null,
          auto_kasko_monthly: null,
          registration_total: null,
          registration_monthly: null,
          mandatory_equipment_total: null,
          mandatory_equipment_monthly: null,
          ao_personal_insurance_total: null,
          ao_personal_insurance_monthly: null,
          green_card_total: null,
          green_card_monthly: null,
          summer_tires_total: null,
          summer_tires_monthly: null,
          winter_tires_total: null,
          winter_tires_monthly: null,
          tire_storage_total: null,
          tire_storage_monthly: null,
          tire_replacement_total: null,
          tire_replacement_monthly: null,
          mechanical_protection_total: null,
          mechanical_protection_monthly: null,
          electronic_protection_total: null,
          electronic_protection_monthly: null,
          gps_tracking_total: null,
          gps_tracking_monthly: null,
          gps_installation_total: null,
          gps_installation_monthly: null,
          regular_service_total: null,
          regular_service_monthly: null,
          emergency_maintenance_total: null,
          emergency_maintenance_monthly: null,
          extended_warranty_total: null,
          extended_warranty_monthly: null,
          jockey_service_total: null,
          jockey_service_monthly: null,
          amss_card_total: null,
          amss_card_monthly: null,
          // Replacement vehicle fields
          replacement_vehicle_type_id: null,
          replacement_vehicle_days: null,
          replacement_vehicle_total: null,
          replacement_vehicle_monthly: null,
          // Tire type fields
          tire_type_id: null,
          // Financing calculation fields
          vehicle_value_with_vat: null,
          vehicle_value_without_vat: null,
          participation_percentage: null,
          participation_amount: null,
          financing_amount: null,
          residual_value_percentage: null,
          residual_value_amount: null,
          period_months: 48,
          reprogramming_note: 'Ne reprogramira se',
          interest_rate: null,
          vat_financing_rate: null,
          processing_fee_percentage: null,
          processing_fee_amount: null,
          monthly_installment_net: null,
          financed_pdv_monthly: null,
          // Total costs fields
          total_cost: null,
          monthly_cost: null,
          margin_percentage: null,
          total_margin: null,
          monthly_margin: null,
          total_cost_with_margin: null,
          monthly_cost_with_margin: null,
          europcar_fee_percentage: null,
          total_europcar_fee: null,
          monthly_europcar_fee: null,
          // Financing fields
          participation_pct: 0,
          participation_eur: null,
          financed_pdv: null,
          // Installments
          installment_value: null,
          installment_difference: null,
          first_installment_bruto: null,
          last_installment_bruto: null,
          first_installment_neto: null,
          last_installment_neto: null,
          regular_installment_bruto: null,
          regular_installment_neto: null,
          // Totals
          total_financing_cost: null,
          total_all_costs: null,
          total_bruto_monthly: null,
          total_neto_monthly: null,
        },
      ];
      this.activeCalc = 0;
      
      // Set default dropdown values for new form
      this.setDefaultDropdownValues();
      
      // Perform initial calculations
      this.performGeneralCalculations();
      
      // Update totals fields
      this.updateTotalsFields();
      
      // Perform all calculation logic for the new calculation
      this.performCalculations();
    } else {
      // Load existing data
      await this.loadData();
    }
  },
};
</script>

<style scoped>
.page {
  max-width: 1200px;
  margin-left: 30px;
  display: flow-root;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.ltr-form-container {
  background-color: #ffffff;
  border-radius: 0.75rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  overflow: visible; /* Allow dropdowns to extend beyond container */
}

.form-header {
  padding: 2rem;
}

.form-title {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
}

.form-subtitle {
  font-size: 1.125rem;
  color: #6b7280;
  margin-bottom: 2rem;
}

.general-info-section {
  border-top: 1px solid #e5e7eb;
  padding-top: 1.5rem;
  margin-bottom: 2rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
}

.nav-tabs .nav-link { 
  cursor: pointer; 
}

.tab-content { 
  background: #fff; 
}

.form-section {
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.5rem;
  background-color: #fafafa;
  margin-bottom: 1.5rem;
}

.section-header {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.75rem;
  margin-bottom: 1.5rem;
}

.total-section {
  background-color: #f0fdf4;
  border-color: #bbf7d0;
}

.total-section .section-header {
  color: #166534;
  border-color: #dcfce7;
}

.form-section.total-costs-section {
  background-color: #fefce8;
  border-color: #fde047;
}

.total-costs-section .section-header {
  color: #854d0e;
  border-color: #fef08a;
}

.maintenance-group {
  margin-bottom: 2rem;
}

.maintenance-group:last-child {
  margin-bottom: 0;
}

.maintenance-group-title {
  font-size: 1rem;
  font-weight: 600;
  color: #166534;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e0e7ff;
}

.maintenance-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.maintenance-grid.large {
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
}

.maintenance-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.maintenance-item.full-width {
  grid-column: 1 / -1; /* Span all columns */
}

.maintenance-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.maintenance-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.total-costs-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
}

.total-cost-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.total-cost-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.total-cost-inputs {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.total-cost-inputs .input-group {
  display: flex;
}

.total-cost-inputs .input-group .form-control {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.total-cost-inputs .input-group-text {
  background-color: #f3f4f6;
  border-left: none;
  font-size: 0.875rem;
  color: #6b7280;
}

.total-price-display {
  border-top: 2px solid #007bff;
  padding-top: 1rem;
}

.total-price-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;
}

.total-price-label {
  font-size: 1.25rem;
  font-weight: 600;
  color: #495057;
  margin: 0;
}

.total-price-inputs {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.total-price-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #007bff;
  background-color: #ffffff;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  border: 1px solid #007bff;
}

.total-price-monthly {
  font-size: 1.125rem;
  font-weight: 600;
  color: #6c757d;
  background-color: #ffffff;
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  border: 1px solid #6c757d;
}

.installments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.installment-group {
  padding: 1rem;
  border-radius: 0.5rem;
  background-color: #f8fafc;
}

.bruto-group {
  background-color: #eef2ff;
}

.neto-group {
  background-color: #f0f9ff;
}

.installment-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #4f46e5;
  margin-bottom: 0.5rem;
}

.installment-inputs {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.installment-values {
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  border: 1px solid #dee2e6;
}

.installment-value-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.installment-value-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.installment-value-amount {
  font-size: 1.25rem;
  font-weight: 700;
  color: #495057;
}

/* Installments Styling */
.installments-summary {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  border: 1px solid #dee2e6;
  margin-bottom: 2rem;
  text-align: end;
}

.summary-text {
  font-size: 1rem;
  color: #495057;
}

.summary-text strong {
  color: #212529;
  font-weight: 600;
}

.summary-text .value {
  font-weight: 700;
  color: #28a745;
}

.installments-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.installment-group {
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  overflow: hidden;
}

.installment-group-title {
  background-color: #495057;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
  padding: 0.75rem;
  margin: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
}

.installment-group:nth-child(2) .installment-group-title {
  background-color: #6c757d;
}

.percentage-indicator {
  font-size: 0.875rem;
  font-weight: 500;
  opacity: 0.9;
}

.installment-items {
  padding: 1rem;
}

.installment-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.installment-item:last-child {
  margin-bottom: 0;
}

.installment-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #495057;
  min-width: 60px;
  margin: 0;
}

.installment-value-display {
  flex: 1;
}

.installment-value-display .form-control {
  background-color: #f8f9fa;
  border-color: #dee2e6;
  text-align: right;
  font-weight: 600;
  color: #495057;
}

/* Form control overrides */
.form-control:focus {
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
}

.form-control:read-only {
  background-color: #f3f4f6;
  color: #4b5563;
}

input[type="number"] {
  text-align: right;
}

/* Replacement vehicle controls */
.replacement-vehicle-controls {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.replacement-vehicle-controls .form-select {
  font-size: 0.875rem;
}

/* Replacement vehicle inline controls */
.replacement-vehicle-inline-controls {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.5rem;
  align-items: flex-start;
}

.replacement-vehicle-inline-controls .form-control {
  height: 38px; /* Match standard form control height */
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
}

.replacement-vehicle-inline-controls .dropdown-container {
  height: 38px;
}

/* Tire type inline controls */
.tire-type-inline-controls {
  display: flex;
  align-items: flex-start;
}

.tire-type-inline-controls .dropdown-container,
.tire-type-inline-controls .v-select,
.tire-type-inline-controls > * {
  height: 38px;
  width: 340px !important;
  max-width: 340px;
}

/* Dropdown z-index fix to appear above form sections */
.replacement-vehicle-inline-controls .v-select,
.replacement-vehicle-inline-controls .dropdown-container,
.tire-type-inline-controls .v-select,
.tire-type-inline-controls .dropdown-container {
  position: relative;
  z-index: 1000;
}

.replacement-vehicle-inline-controls .v-select .vs__dropdown-menu,
.replacement-vehicle-inline-controls .dropdown-menu,
.tire-type-inline-controls .v-select .vs__dropdown-menu,
.tire-type-inline-controls .dropdown-menu {
  position: absolute;
  z-index: 1001;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Ensure maintenance section allows dropdown overflow */
.maintenance-group {
  position: relative;
  overflow: visible;
}

.form-section {
  overflow: visible;
}

/* Total section field consistency */
.total-section .form-control {
  min-width: 200px;
  max-width: 300px;
  width: 100%;
}

.total-section .form-group {
  min-width: 200px;
  max-width: 300px;
}

.total-section .row .col,
.total-section .row .col-md-6 {
  min-width: 250px;
  max-width: 350px;
  flex: 1 1 300px;
}

/* Collapsible sections */
.collapsible-header {
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  user-select: none;
}

.collapsible-header:hover {
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 0.5rem;
  /* margin: -0.5rem; */
  padding: 0.5rem;
}

.collapsible-header i {
  font-size: 1rem;
  transition: transform 0.3s ease;
  color: #6b7280;
}

.section-content {
  transition: all 0.3s ease;
  overflow: visible; /* Allow dropdowns to extend beyond section */
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .page {
    margin-left: 15px;
    margin-right: 15px;
  }
  
  .form-header {
    padding: 1rem;
  }
  
  .form-title {
    font-size: 1.5rem;
  }
  
  .maintenance-grid {
    grid-template-columns: 1fr;
  }
  
  .total-costs-grid {
    grid-template-columns: 1fr;
  }
  
  .installments-grid {
    grid-template-columns: 1fr;
  }
  
  .tab-nav {
    flex-wrap: wrap;
  }
}

/* Replacement vehicle inline controls */
.replacement-vehicle-inline-controls {
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  gap: 0.5rem;
  align-items: flex-start;
}

.replacement-vehicle-inline-controls > * {
  width: 100%;
  height: 38px;
}

.replacement-vehicle-inline-controls .dropdown-container,
.replacement-vehicle-inline-controls .v-select {
  min-width: 0;
}

@media (max-width: 768px) {
  .replacement-vehicle-inline-controls {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}
</style>
